import React, { useState } from 'react';

import { ChevronRight } from 'lucide-react';

import { v9 } from '../v9';

import PartnerSelect from './PartnerSelect';

import type {
	PricingConfiguration,
	TemplateRiskTier,
	TemplateStatus,
} from '../../../types';

const TEMPLATE_RISK_TIERS: TemplateRiskTier[] = [
	'LOW',
	'MEDIUM',
	'HIGH',
	'OTHER',
];
const TEMPLATE_STATUSES: { value: TemplateStatus; label: string }[] = [
	{ value: 'CUSTOM', label: 'Custom' },
	{ value: 'DRAFT', label: 'Draft' },
	{ value: 'ARCHIVED', label: 'Archived' },
];
const INACTIVE_STATUSES: TemplateStatus[] = ['DRAFT', 'ARCHIVED'];

interface BasicInfoStepProps {
	configuration: PricingConfiguration;
	onUpdate: (updates: Partial<PricingConfiguration>) => void;
	onNext: () => void;
	onCancel?: () => void;
	hideNavigation?: boolean;
	// Override the flag checkbox. Rate configs use `is_default`; revenue configs
	// use `is_active`. When these are passed, the checkbox drives the caller's
	// field instead of defaulting to `configuration.is_default`.
	flagChecked?: boolean;
	onFlagChange?: (checked: boolean) => void;
	flagLabel?: string;
	// Pricing templates show partner and risk tier (ROC-86).
	showProgramFields?: boolean;
	title?: string;
	subtitle?: string;
	nameLabel?: string;
	namePlaceholder?: string;
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({
	configuration,
	onUpdate,
	onNext,
	onCancel,
	hideNavigation = false,
	flagChecked,
	onFlagChange,
	flagLabel,
	showProgramFields = false,
	title = 'Basic details',
	subtitle = 'Name and save your pricing template for future use',
	nameLabel = 'Template name',
	namePlaceholder = 'Template name',
}) => {
	const [nameError, setNameError] = useState('');

	// Default to the rate-config `is_default` field when no override is supplied.
	// Coerce to false so the checkbox is always controlled (avoids React's
	// "uncontrolled -> controlled" warning when both are undefined).
	const isFlagChecked = flagChecked ?? configuration.is_default ?? false;
	const handleFlagChange = (checked: boolean) =>
		onFlagChange ? onFlagChange(checked) : onUpdate({ is_default: checked });
	const checkboxLabel = flagLabel ?? 'Set as default configuration';

	const handleNext = () => {
		// Validate required fields
		if (!configuration.name.trim()) {
			setNameError(`${nameLabel} is required`);
			return;
		}
		setNameError('');
		onNext();
	};

	const handleNameChange = (name: string) => {
		onUpdate({ name });
		if (nameError && name.trim()) {
			setNameError('');
		}
	};

	return (
		<div>
			<div className={v9.wizardTitle}>
				<h3 className={v9.wizardH3}>{title}</h3>
				<p className={v9.wizardP}>{subtitle}</p>
			</div>

			<div className="mx-auto max-w-[512px] space-y-4">
				<div>
					<label htmlFor="configName" className={v9.label}>
						{nameLabel} <span className="text-red-500">*</span>
					</label>
					<input
						id="configName"
						type="text"
						value={configuration.name}
						onChange={(e) => handleNameChange(e.target.value)}
						placeholder={namePlaceholder}
						className={`${v9.inputLg} ${nameError ? 'border-red-300 bg-red-50' : ''}`}
					/>
					{nameError && (
						<p className="mt-1 text-xs text-red-700">{nameError}</p>
					)}
				</div>

				<div>
					<label htmlFor="configDescription" className={v9.label}>
						Description
					</label>
					<textarea
						id="configDescription"
						value={configuration.description}
						onChange={(e) => onUpdate({ description: e.target.value })}
						placeholder="Describe the configuration"
						rows={3}
						className={`${v9.inputLg} resize-none`}
					/>
				</div>

				{showProgramFields && (
					<div className="grid grid-cols-2 gap-4">
						<div>
							<label htmlFor="templatePartner" className={v9.label}>
								Partner
							</label>
							<PartnerSelect
								id="templatePartner"
								value={configuration.partner_id ?? ''}
								onChange={(partner_id) => onUpdate({ partner_id })}
							/>
						</div>
						<div>
							<label htmlFor="templateRiskTier" className={v9.label}>
								Risk tier
							</label>
							<select
								id="templateRiskTier"
								className={v9.input}
								value={configuration.risk_tier ?? ''}
								onChange={(e) =>
									onUpdate({
										risk_tier: e.target.value as TemplateRiskTier | '',
									})
								}
							>
								<option value="">—</option>
								{TEMPLATE_RISK_TIERS.map((tier) => (
									<option key={tier}>{tier}</option>
								))}
							</select>
						</div>
						<div className="col-span-2">
							<label htmlFor="templateStatus" className={v9.label}>
								Status
							</label>
							<select
								id="templateStatus"
								className={v9.input}
								value={configuration.status ?? 'CUSTOM'}
								onChange={(e) => {
									const status = e.target.value as TemplateStatus;
									// A draft or archived template cannot be the default.
									onUpdate(
										INACTIVE_STATUSES.includes(status)
											? { status, is_default: false }
											: { status },
									);
								}}
							>
								{TEMPLATE_STATUSES.map(({ value, label }) => (
									<option key={value} value={value}>
										{label}
									</option>
								))}
							</select>
						</div>
					</div>
				)}

				<label className={v9.check}>
					<input
						type="checkbox"
						checked={isFlagChecked}
						disabled={
							showProgramFields &&
							INACTIVE_STATUSES.includes(configuration.status ?? 'CUSTOM')
						}
						onChange={(e) => handleFlagChange(e.target.checked)}
						className={v9.checkInput}
					/>
					{checkboxLabel}
				</label>
			</div>

			{/* Navigation - Only show if not hidden */}
			{!hideNavigation && (
				<div className="flex justify-between items-center pt-8 mt-12">
					<button
						onClick={onCancel}
						className="text-gray-500 hover:text-gray-700 transition-colors font-medium"
					>
						Cancel
					</button>
					<div className="flex items-center text-gray-500">
						<span className="text-sm font-medium">Step 1 of 4</span>
					</div>
					<button
						onClick={handleNext}
						className="flex items-center px-8 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors font-medium shadow-lg"
					>
						Next
						<ChevronRight className="w-5 h-5 ml-2" />
					</button>
				</div>
			)}
		</div>
	);
};

export default BasicInfoStep;
